import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {Box, Typography} from '@mui/material';

import {MatchMetaItem} from './MatchMetaItem';
import {empionGreen, empionOrange} from '../../../../../layouts/theme/EmpionColors';
import {AppConfig} from '../../../../../lib/AppConfig';
import {transformDate} from '../../../../../utils/dateUtils/dateFormattingUtils';
import {getMatchStatusColor} from '../../../../../utils/matchUtils';
import {MatchStatusLabel} from '../../../../company/recruiting/talent-pool/MatchStatusLabel';
import {Match} from '../../types/ApplicantMatchesTypes';

interface Props {
  match: Match;
}

export const ApplicantMatchCard = ({match}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'applicantDashboard.matchesView'});
  const navigate = useNavigate();

  const to = `${AppConfig.APPLICANT_MATCHES_PATH}/${match.resourceId}`;
  const city = match.companyJobPosting.locations[0]?.city ?? '—';
  const salary = match.companyJobPosting.salary;
  const salaryLabel = salary?.min || salary?.max ? `${salary.min} - ${salary.max}` : '—';
  const dotColor = getMatchStatusColor(match.status);

  return (
    <Box
      role='button'
      tabIndex={0}
      onClick={() => navigate(to, {state: {match}})}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          navigate(to, {state: {match}});
        }
      }}
      sx={{
        cursor: 'pointer',
        border: `1px solid ${empionOrange[3]}`,
        borderRadius: '12px',
        p: 2,
        bgcolor: 'white',
        '&:active': {bgcolor: empionOrange[7]},
      }}
    >
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25}}>
        <Box sx={{minWidth: 0, flexGrow: 1}}>
          <Typography sx={{color: empionGreen[700], fontWeight: 700, fontSize: '15px'}} noWrap>
            {match.company.name}
          </Typography>
          <Typography sx={{color: empionGreen[100], fontSize: '13px'}} noWrap>
            {match.companyJobPosting.title || '—'}
          </Typography>
        </Box>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0}}>
          <Box sx={{width: 10, height: 10, borderRadius: '50%', bgcolor: dotColor}} />
          <Typography sx={{color: empionGreen[700], fontSize: '13px', textTransform: 'capitalize'}} noWrap>
            <MatchStatusLabel status={match.status} />
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          mt: 2,
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 1.5,
        }}
      >
        <MatchMetaItem label={t('tableColumns.location')} value={city} />
        <MatchMetaItem label={t('tableColumns.salary')} value={salaryLabel} />
        <MatchMetaItem
          label={t('tableColumns.cultureFit')}
          value={match.culturalFitScore ? String(match.culturalFitScore) : '—'}
        />
        <MatchMetaItem
          label={t('tableColumns.matched')}
          value={match.createdAt ? transformDate(match.createdAt, 'numeric') : '—'}
        />
      </Box>
    </Box>
  );
};
